import { useTranslation } from 'react-i18next';
import Typography from '../../sharedComponents/Typography/Typography';
import {
  type PayoutAccount,
  PayoutProvider,
  PayoutProviderDisplay,
} from '../../utils/types/Entity';
import { useMemo, useState } from 'react';
import {
  AddPayoutAccountButton,
  AddPayoutProviderAccountComponent,
} from '../../components';
import { useActions, useAppSelector } from '../../store/hooks';
import { AddPayoutAccountContainerProps } from './AddPayoutAccountContainer.types';
import styles from './add-payout-account-container.module.scss';
import { getAvailableProviders } from '../../store/slices';

const AddPayoutAccountContainer: React.FC<AddPayoutAccountContainerProps> = ({
  currency,
  onAccountCreationSuccess,
  processingRequest,
  onHideAccountList,
}): JSX.Element => {
  const { t } = useTranslation();
  const [providerModule, setProviderModule] =
    useState<PayoutProviderDisplay | null>(null);
  const availableProviders = useAppSelector(getAvailableProviders(currency));
  const payoutAccountsLoaded = useAppSelector(
    (state) => state.payoutAccount.loadedPayoutAccounts,
  );
  const dispatch = useActions();
  const { payoutAccounts } = useAppSelector((state) => state.payoutAccount);

  const providerButtons = useMemo((): PayoutProviderDisplay[] => {
    const bankProviders = [PayoutProvider.DWOLLA, PayoutProvider.WISE];

    const hasPayPalAccount = payoutAccounts.some(
      (pa) => pa.providerName === PayoutProvider.PayPal,
    );

    const payPalAvailable =
      !hasPayPalAccount && availableProviders.includes(PayoutProvider.PayPal);
    const bankAvailable = bankProviders.some((provider) =>
      availableProviders.includes(provider),
    );

    return [
      ...(bankAvailable ? [PayoutProviderDisplay.Bank] : []),
      ...(payPalAvailable ? [PayoutProviderDisplay.PayPal] : []),
    ];
  }, [availableProviders, payoutAccounts]);

  if (!payoutAccountsLoaded) {
    return <></>;
  }

  const onAccountCreationSuccessWrapper = (
    payoutAccount: PayoutAccount,
  ): void => {
    dispatch.addPayoutAccount(payoutAccount);

    setProviderModule(null);
    onHideAccountList(false);

    if (onAccountCreationSuccess) {
      onAccountCreationSuccess(payoutAccount);
    }
  };

  return (
    <div className={styles.container}>
      <Typography variant="h5">{t('label.add_new_account')}</Typography>

      {!availableProviders.length && (
        <Typography
          variant="body-2"
          className={styles.noProvidersAvailableText}
        >
          {t('sunshine.label.no_providers_available')}
        </Typography>
      )}

      <div className={styles.contentWrapper}>
        <div
          className={`${styles.providerButtonList} ${
            !providerModule ? styles.visibleLeft : styles.hiddenLeft
          }`}
        >
          {providerButtons.map((av, i) => {
            return (
              <AddPayoutAccountButton
                key={`${i}_${av}`}
                providerLabel
                provider={av}
                clickHandler={(): void => {
                  setProviderModule(av);
                  onHideAccountList(true);
                }}
                colorVariant="snow"
                disabled={processingRequest}
              />
            );
          })}
        </div>

        <div
          className={`${styles.providerModule} ${
            providerModule ? styles.visibleRight : styles.hiddenRight
          }`}
        >
          {providerModule ? (
            <AddPayoutProviderAccountComponent
              provider={providerModule}
              onBackClick={(): void => {
                setProviderModule(null);
                onHideAccountList(false);
              }}
              onAccountCreationSuccess={onAccountCreationSuccessWrapper}
            />
          ) : (
            <></>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddPayoutAccountContainer;
